import React, { useCallback, useEffect, useRef, useState } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

// Tiptap v3 ships React 19 typings; the admin runs React 18 : the runtime is
// compatible, only the JSX component type needs realigning.
const TiptapContent = EditorContent as unknown as React.FC<{ editor: Editor | null; className?: string }>;
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { FiBold, FiItalic, FiLink, FiRotateCcw, FiSmile, FiUnderline, FiX } from "react-icons/fi";
import { INLINE_ICON_LABELS, INLINE_ICON_NAMES, inlineIconSvg } from "@bcc/core-renderers";
import {
  EnterInsertsBreak,
  InlineIcon,
  SingleParagraphDocument,
  isSafeEditorHref,
  tiptapDocToInlineHtml,
} from "./richTextExtensions";

export type ParagraphRichEditorProps = {
  value: string;
  onChange: (html: string) => void;
  minHeight?: string;
  placeholder?: string;
  className?: string;
};

const TOOLBAR_BUTTON =
  "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-semibold transition hover:bg-white hover:shadow disabled:cursor-not-allowed disabled:opacity-50";

function toolbarButtonClass(active: boolean): string {
  return `${TOOLBAR_BUTTON} ${active ? "bg-white text-[#005CA9] shadow" : "text-slate-700"}`;
}

/**
 * Inline rich-text editor for paragraph-grade content (TextBlock items,
 * accordion paragraphs…): ONE paragraph, real bold/italic/underline, safe
 * links and inline icon markers. Enter inserts a line break; the produced
 * HTML is the inline string the content model stores, sanitised again at
 * save time (`sanitizeParagraphHtml`) and at render time in the SDK.
 */
export const ParagraphRichEditor: React.FC<ParagraphRichEditorProps> = ({
  value,
  onChange,
  minHeight = "120px",
  placeholder = "Saisissez le texte…",
  className = "",
}) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [linkError, setLinkError] = useState(false);
  const [iconOpen, setIconOpen] = useState(false);
  // onChange identity changes on every parent render; the editor is built once.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      // StarterKit minus its document (replaced by the single-paragraph top
      // node) and minus every block-level tool : this editor is inline-only.
      StarterKit.configure({
        document: false,
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        code: false,
        codeBlock: false,
        strike: false,
        horizontalRule: false,
      }),
      SingleParagraphDocument,
      EnterInsertsBreak,
      Underline,
      Link.configure({ openOnClick: false, autolink: false, linkOnPaste: true }),
      InlineIcon,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "min-h-0 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400 [&_a]:text-[#005CA9] [&_a]:underline",
        style: `min-height: ${minHeight}`,
        "data-placeholder": placeholder,
      },
    },
    onUpdate: ({ editor: e }) => {
      onChangeRef.current(tiptapDocToInlineHtml(e.getHTML()));
    },
  });

  // External value changes (version load, item switch) reset the document.
  useEffect(() => {
    if (!editor) return;
    if (tiptapDocToInlineHtml(editor.getHTML()) !== (value ?? "")) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [editor, value]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const href = linkValue.trim();
    if (!isSafeEditorHref(href)) {
      setLinkError(true);
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setLinkOpen(false);
    setLinkValue("");
    setLinkError(false);
  }, [editor, linkValue]);

  const openLinkPanel = useCallback(() => {
    if (!editor) return;
    setLinkValue((editor.getAttributes("link").href as string) ?? "");
    setLinkError(false);
    setIconOpen(false);
    setLinkOpen((open) => !open);
  }, [editor]);

  const removeLink = useCallback(() => {
    editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    setLinkOpen(false);
    setLinkValue("");
  }, [editor]);

  const insertIcon = useCallback(
    (name: string) => {
      editor?.chain().focus().insertContent({ type: "inlineIcon", attrs: { name } }).run();
      setIconOpen(false);
    },
    [editor],
  );

  const clearFormatting = useCallback(() => {
    editor?.chain().focus().unsetAllMarks().run();
  }, [editor]);

  return (
    <div className={`flex min-h-0 flex-col ${className}`.trim()}>
      <div className="mb-2 shrink-0 flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
        <button
          type="button"
          onClick={() => editor?.chain().focus().toggleBold().run()}
          className={toolbarButtonClass(Boolean(editor?.isActive("bold")))}
          title="Gras"
          aria-label="Gras"
        >
          <FiBold className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          className={toolbarButtonClass(Boolean(editor?.isActive("italic")))}
          title="Italique"
          aria-label="Italique"
        >
          <FiItalic className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          className={toolbarButtonClass(Boolean(editor?.isActive("underline")))}
          title="Souligné"
          aria-label="Souligné"
        >
          <FiUnderline className="h-4 w-4" />
        </button>
        <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
        <button
          type="button"
          onClick={openLinkPanel}
          className={toolbarButtonClass(Boolean(editor?.isActive("link")) || linkOpen)}
          title="Lien"
          aria-label="Lien"
        >
          <FiLink className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => {
            setLinkOpen(false);
            setIconOpen((open) => !open);
          }}
          className={toolbarButtonClass(iconOpen)}
          title="Insérer une icône"
          aria-label="Insérer une icône"
        >
          <FiSmile className="h-4 w-4" />
        </button>
        <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
        <button
          type="button"
          onClick={clearFormatting}
          className={`${TOOLBAR_BUTTON} text-slate-600`}
          title="Effacer le format"
        >
          <FiRotateCcw className="h-4 w-4" />
          Effacer le format
        </button>
      </div>

      {linkOpen && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
          <input
            type="text"
            value={linkValue}
            onChange={(e) => {
              setLinkValue(e.target.value);
              setLinkError(false);
            }}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyLink())}
            placeholder="https://… ou /chemin-interne"
            className={`min-w-0 flex-1 rounded-md border px-2.5 py-1.5 text-sm outline-none focus:border-slate-400 ${
              linkError ? "border-rose-300 bg-rose-50" : "border-slate-200"
            }`}
            aria-label="Adresse du lien"
          />
          <button
            type="button"
            onClick={applyLink}
            className="rounded-md bg-[#005CA9] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[#0F2849]"
          >
            Appliquer
          </button>
          {editor?.isActive("link") && (
            <button
              type="button"
              onClick={removeLink}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              <FiX className="h-4 w-4" />
              Retirer le lien
            </button>
          )}
          {linkError && <p className="w-full text-xs text-rose-600">Adresse invalide ou non autorisée.</p>}
        </div>
      )}

      {iconOpen && (
        <div className="mb-2 grid grid-cols-6 gap-1 rounded-lg border border-slate-200 bg-white p-2 sm:grid-cols-12">
          {INLINE_ICON_NAMES.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => insertIcon(name)}
              title={INLINE_ICON_LABELS[name] ?? name}
              aria-label={INLINE_ICON_LABELS[name] ?? name}
              className="flex h-9 items-center justify-center rounded-md text-[#005CA9] transition hover:bg-slate-100"
              // Trusted static SVG from the SDK catalogue : never user content.
              dangerouslySetInnerHTML={{ __html: inlineIconSvg(name) }}
            />
          ))}
        </div>
      )}

      <TiptapContent editor={editor as Editor | null} className="min-h-0 flex-1 [&_.bcc-inline-icon]:inline-block [&_.bcc-inline-icon]:h-[1em] [&_.bcc-inline-icon]:w-[1em]" />
    </div>
  );
};
